import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { setCategories, setGenres } from '../../store/slices/cacheSlice';
import { closeMobileMenu, toggleSidebar } from '../../store/slices/layoutSlice';
import { setKeyword } from '../../store/slices/searchSlice';
import styles from './Sidebar.module.css';

// Import JSON data
import dramaData from '../../data/drama-full-1-short-dramas.json';

// 导入热门genres列表
const HOT_GENRES = [
  'Urban',
  'Revenge',
  'Domineering CEO',
  'Gangster',
  'Love',
  'Love triangle',
  'Suspense',
  'Legacy',
  'Love of trillionaire',
  'Murder'
];

const Sidebar: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation(); // 获取当前路径
  const { sidebarOpen } = useSelector((state: RootState) => state.layout);
  const { categories, genres } = useSelector((state: RootState) => state.cache);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isMobileSmall, setIsMobileSmall] = useState(window.innerWidth <= 576);
  const [searchValue, setSearchValue] = useState('');
  const [_scrollPosition, setScrollPosition] = useState(0); // 存储滚动位置
  const sidebarRef = useRef<HTMLElement>(null); // 侧边栏引用

  // 添加调试日志
  useEffect(() => {
    console.log('Sidebar state:', {
      sidebarOpen,
      isMobile,
      isMobileSmall,
      windowWidth: window.innerWidth
    });
  }, [sidebarOpen, isMobile, isMobileSmall]);

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 768;
      setIsMobile(newIsMobile);
      setIsMobileSmall(window.innerWidth <= 576);
      
      // 如果从移动端切换到桌面端，并且侧边栏是打开的，确保恢复页面滚动
      if (!newIsMobile && sidebarOpen) {
        enableBodyScroll();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      enableBodyScroll();
    };
  }, [sidebarOpen]);

  // 控制侧边栏打开/关闭时的页面滚动
  useEffect(() => {
    if (isMobile) {
      if (sidebarOpen) {
        disableBodyScroll();
      } else {
        enableBodyScroll();
      }
    }
  }, [sidebarOpen, isMobile]);

  // 添加点击事件监听，用于点击遮罩关闭侧边栏
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobile && sidebarOpen && sidebarRef.current) {
        const target = event.target as HTMLElement;
        // 检查点击是否来自菜单按钮
        const menuButton = document.querySelector(`.${styles.menuToggle}`);
        if (menuButton && menuButton.contains(target)) {
          return;
        }
        // 检查点击是否在侧边栏内部
        if (!sidebarRef.current.contains(target)) {
          console.log('Click outside detected, closing sidebar');
          dispatch(toggleSidebar());
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [dispatch, isMobile, sidebarOpen]);

  // 禁用页面滚动的函数
  const disableBodyScroll = () => {
    // 保存当前滚动位置
    setScrollPosition(window.pageYOffset);
    
    // 只设置overflow为hidden，不改变position，这样不会导致视觉上的跳动
    document.body.style.overflow = 'hidden';
  };

  // 启用页面滚动的函数
  const enableBodyScroll = () => {
    // 恢复body样式
    document.body.style.overflow = '';
    
    // 无需恢复滚动位置，因为我们没有改变它
  };

  useEffect(() => {
    // Extract unique categories from data
    if (categories.length === 0) {
      const allCategories = new Set<string>();
      
      dramaData.data.forEach(item => {
        item.categories.forEach(category => {
          allCategories.add(category);
        });
      });
      
      dispatch(setCategories(Array.from(allCategories).sort()));
    }

    // 设置genres数据
    if (genres.length === 0) {
      // 使用预定义的热门genres
      dispatch(setGenres(HOT_GENRES));
    }
  }, [dispatch, categories.length, genres.length]);

  // 检查链接是否处于活动状态
  const isActive = (path: string): boolean => {
    if (path === '/' && location.pathname === '/') {
      return true;
    }
    if (path !== '/' && location.pathname.startsWith(path)) {
      return true;
    }
    return false;
  };

  // 检查分类链接是否处于活动状态
  const _isCategoryActive = (category: string): boolean => {
    const encodedCategory = encodeURIComponent(category);
    return location.search.includes(`category=${encodedCategory}`);
  };

  // 检查流派链接是否处于活动状态
  const isGenreActive = (genre: string): boolean => {
    const encodedGenre = encodeURIComponent(genre);
    // 使用正则表达式确保完全匹配，避免子字符串匹配问题
    const regex = new RegExp(`[?&]genre=${encodedGenre}(&|$)`);
    return regex.test(location.search);
  };

  const handleLinkClick = () => {
    // 关闭移动端菜单
    dispatch(closeMobileMenu());
    
    // 如果是移动设备，则关闭侧边栏
    if (isMobile) {
      dispatch(toggleSidebar());
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // 无论搜索框是否为空，都导航到搜索页面
    // 如果有搜索关键词，则设置到store中
    if (searchValue.trim()) {
      dispatch(setKeyword(searchValue.trim()));
      navigate(`/search?keyword=${encodeURIComponent(searchValue.trim())}`);
    } else {
      dispatch(setKeyword(''));
      navigate('/search');
    }
    // 关闭侧边栏
    if (isMobile) {
      dispatch(toggleSidebar());
    }
  };

  // 创建遮罩点击处理函数
  const handleOverlayClick = (e: React.MouseEvent) => {
    console.log('Overlay clicked');
    if (e.target === e.currentTarget) {
      console.log('Dispatching toggleSidebar');
      dispatch(toggleSidebar());
    }
  };

  return (
    <aside 
      ref={sidebarRef}
      className={`${styles.sidebar} ${sidebarOpen ? styles.open : ''}`}
      onClick={isMobile ? handleOverlayClick : undefined}
    >
      <div 
        className={styles.sidebarContent} 
        onClick={(e) => {
          e.stopPropagation();
          console.log('Sidebar content clicked');
        }}
      >
        {/* 移动端搜索框 */}
        {isMobileSmall && (
          <div className={styles.mobileSearchContainer}>
            <form className={styles.mobileSearchForm} onSubmit={handleSearchSubmit}>
              <input
                type="text"
                placeholder="Search for dramas, videos..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className={styles.mobileSearchInput}
              />
              <button type="submit" className={styles.mobileSearchButton} aria-label="Search">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 12C9.76142 12 12 9.76142 12 7C12 4.23858 9.76142 2 7 2C4.23858 2 2 4.23858 2 7C2 9.76142 4.23858 12 7 12Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 14L11 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </form>
          </div>
        )}

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Browse</h3>
          <ul className={styles.navList}>
            <li>
              <a 
                href="/" 
                className={`${styles.navLink} ${isActive('/') ? styles.active : ''}`} 
                onClick={handleLinkClick}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Home
              </a>
            </li>
            <li>
              <a 
                href="/search?category=New Release" 
                className={`${styles.navLink} ${location.pathname === '/search' && location.search.includes('category=New%20Release') ? styles.active : ''}`} 
                onClick={handleLinkClick}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 18V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M4.93 4.93L7.76 7.76" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16.24 16.24L19.07 19.07" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M18 12H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M4.93 19.07L7.76 16.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                New Releases
              </a>
            </li>
            <li>
              <a 
                href="/search?category=Trending Now" 
                className={`${styles.navLink} ${location.pathname === '/search' && location.search.includes('category=Trending%20Now') ? styles.active : ''}`} 
                onClick={handleLinkClick}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Trending
              </a>
            </li>
          </ul>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Genres</h3>
          <ul className={styles.categoryList}>
            {genres.map(genre => (
              <li key={genre}>
                <a 
                  href={`/search?genre=${encodeURIComponent(genre)}`} 
                  className={`${styles.categoryLink} ${isGenreActive(genre) ? styles.active : ''}`}
                  onClick={handleLinkClick}
                >
                  {genre}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar; 